import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboard, getMyWorkOrders, seedData } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import DailyProgressWidget from '../components/DailyProgressWidget';
import { 
  ClipboardList, 
  AlertTriangle, 
  Bell, 
  Users, 
  Cloud,
  ChevronRight,
  Plus,
  Zap,
  RefreshCw,
  MessageCircle,
  Archive,
  BookOpen,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user, isSupervisor, isAdmin } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [myWorkOrders, setMyWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user is a crew_leader (not supervisor/admin)
  const isCrewLeaderOnly = user?.role === 'crew_leader';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await getDashboard();
      setDashboard(response.data);
      
      // For crew leaders, also fetch their specific work orders
      if (isCrewLeaderOnly) {
        try {
          const myWoResponse = await getMyWorkOrders();
          setMyWorkOrders(myWoResponse.data);
        } catch (error) {
          console.error('Failed to fetch my work orders:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await seedData();
      toast.success('Demo data created successfully!');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-slate-100 text-slate-800 border-slate-300'
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-slate-100 text-slate-800'
    };
    return colors[priority] || colors.normal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600">Here&apos;s what&apos;s happening with your projects today.</p>
        </div>
        <div className="flex gap-2">
          {isSupervisor() && (
            <Link to="/work-orders/new">
              <Button className="bg-[#0056D2] hover:bg-[#0044A4]" data-testid="new-work-order-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Work Order
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={handleSeedData} data-testid="seed-data-btn">
            <Zap className="w-4 h-4 mr-2" />
            Seed Demo Data
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/work-orders">
          <Card className="border-l-4 border-l-[#0056D2] hover:shadow-md hover:border-[#0056D2] transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Active Work Orders</p>
                  <p className="text-3xl font-bold">{dashboard?.total_active_work_orders || 0}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-[#0056D2]" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/bulletins">
          <Card className="border-l-4 border-l-[#F97316] hover:shadow-md hover:border-[#F97316] transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Unread Alerts</p>
                  <p className="text-3xl font-bold">{dashboard?.unread_notification_count || 0}</p>
                </div>
                <Bell className="w-8 h-8 text-[#F97316]" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/bulletins">
          <Card className="border-l-4 border-l-green-500 hover:shadow-md hover:border-green-500 transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">New Bulletins</p>
                  <p className="text-3xl font-bold">{dashboard?.recent_bulletins?.length || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/work-orders">
          <Card className="border-l-4 border-l-purple-500 hover:shadow-md hover:border-purple-500 transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pending Assignments</p>
                  <p className="text-3xl font-bold">{dashboard?.pending_assignments?.length || 0}</p>
                </div>
                <Users className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Progress Widget - Supervisors/Admins only */}
        {(isSupervisor() || isAdmin?.()) && (
          <div className="lg:col-span-3">
            <DailyProgressWidget />
          </div>
        )}

        {/* Crew Leader Daily Progress Quick Action */}
        {isCrewLeaderOnly && myWorkOrders.length > 0 && (
          <div className="lg:col-span-3">
            <Card className="border-blue-500 bg-slate-800">
              <CardHeader className="pb-3 bg-slate-700/50">
                <CardTitle className="text-lg font-bold flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Daily Progress Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-slate-300 mb-4">
                  Submit your daily progress reports for assigned work orders before 1:00 PM.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {myWorkOrders.slice(0, 6).map((wo) => (
                    <Link key={wo.id} to={`/work-orders/${wo.id}?tab=progress`}>
                      <Card className={`hover:border-blue-400 transition-colors cursor-pointer ${
                        wo.today_update_count > 0 
                          ? 'border-green-500 bg-slate-700' 
                          : 'border-amber-500 bg-slate-700'
                      }`}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-sm font-bold text-blue-400">{wo.wo_number}</span>
                            {wo.today_update_count > 0 ? (
                              <Badge className="bg-green-600 text-white text-xs">
                                ✓ Submitted
                                {wo.today_update_count > 1 && ` (${wo.today_update_count})`}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-600 text-white text-xs">Needs Report</Badge>
                            )}
                          </div>
                          <p className="text-sm text-white truncate">{wo.location_name}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Work Orders List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold">My Work Orders</CardTitle>
              <Link to="/work-orders">
                <Button variant="ghost" size="sm">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {dashboard?.work_orders?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No work orders assigned yet.</p>
                  <p className="text-sm">Click &quot;Seed Demo Data&quot; to create sample data.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {dashboard?.work_orders?.map((wo) => (
                      <Link key={wo.id} to={`/work-orders/${wo.id}`}>
                        <div 
                          className="p-4 border rounded-lg hover:border-[#0056D2] hover:bg-slate-50 transition-all cursor-pointer"
                          data-testid={`work-order-${wo.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-[#0056D2] font-medium">
                                  {wo.wo_number}
                                </span>
                                <Badge className={getStatusColor(wo.status)}>
                                  {wo.status?.replace('_', ' ')}
                                </Badge>
                              </div>
                              <h3 className="font-semibold truncate">{wo.title}</h3>
                              <p className="text-sm text-slate-600">{wo.location_name}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {isSupervisor() && (
                <Link to="/work-orders/new" className="block">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-new-wo">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Work Order
                  </Button>
                </Link>
              )}
              <Link to="/chat" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-chat">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Team Chat
                </Button>
              </Link>
              <Link to="/archive" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-archive">
                  <Archive className="w-4 h-4 mr-2" />
                  Search Archive
                </Button>
              </Link>
              <Link to="/lessons" className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="quick-lessons">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Lessons Learned
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Weather Widget */}
          {dashboard?.work_orders?.[0] ? (
            <Link to={`/work-orders/${dashboard.work_orders[0].id}`}>
              <Card className="weather-clear text-white overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium opacity-90">Today&apos;s Weather</span>
                    <Cloud className="w-5 h-5" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold">72°F</span>
                    <span className="text-sm opacity-75">Clear</span>
                  </div>
                  <p className="text-sm mt-2 opacity-90">
                    Good conditions for outdoor work. Wind: 12 mph
                  </p>
                  <p className="text-xs mt-2 opacity-75 flex items-center gap-1">
                    Click to view project weather <ChevronRight className="w-3 h-3" />
                  </p>
                </CardContent>
              </Card>
            </Link>
          ) : (
            <Card className="weather-clear text-white overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium opacity-90">Today&apos;s Weather</span>
                  <Cloud className="w-5 h-5" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">72°F</span>
                  <span className="text-sm opacity-75">Clear</span>
                </div>
                <p className="text-sm mt-2 opacity-90">
                  Good conditions for outdoor work. Wind: 12 mph
                </p>
              </CardContent>
            </Card>
          )}

          {/* Recent Bulletins */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#F97316]" />
                Recent Bulletins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_bulletins?.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No bulletins</p>
              ) : (
                <div className="space-y-3">
                  {dashboard?.recent_bulletins?.slice(0, 3).map((bulletin) => (
                    <Link key={bulletin.id} to="/bulletins">
                      <div 
                        className="p-3 bg-slate-50 rounded-lg border-l-4 border-l-[#F97316] hover:bg-slate-100 cursor-pointer transition-colors"
                        data-testid={`bulletin-${bulletin.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getPriorityColor(bulletin.priority)}>
                            {bulletin.priority}
                          </Badge>
                          <Badge variant="outline">{bulletin.bulletin_type}</Badge>
                        </div>
                        <h4 className="font-semibold text-sm">{bulletin.title}</h4>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {bulletin.content}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <Link to="/bulletins">
                <Button variant="ghost" className="w-full mt-3" size="sm" data-testid="view-all-bulletins-btn">
                  View All Bulletins <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0056D2]" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.notifications?.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No new notifications</p>
              ) : (
                <div className="space-y-2">
                  {dashboard?.notifications?.slice(0, 5).map((notification) => (
                    <div 
                      key={notification.id} 
                      className="p-2 bg-blue-50 rounded text-sm"
                      data-testid={`notification-${notification.id}`}
                    >
                      {notification.message}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
